import { Box, TextField } from '@mui/material';
import React from 'react';

import InputLabel from '@components/InputLabel';

interface YearInputProps {
  label?: string;
  error?: string;
  value?: string | null;
  onChange?: (value: string | null) => void;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  required?: boolean;
  /** Maximum year */
  min?: number;
  /** Minimum year */
  max?: number;
}

const FORMAT = 'yyyy';

/**
 * Input for picking a year
 */
const YearInput: React.FC<YearInputProps> = ({
  label = '',
  error = '',
  value = '',
  onChange,
  onBlur,
  name,
  required = false,
  min = 1900,
  max = 2100,
}) => {
  const [minYear, setMinYear] = React.useState<number>();
  const [maxYear, setMaxYear] = React.useState<number>();

  React.useEffect(() => {
    setMinYear(min);
    setMaxYear(max);
  }, [max, min]);

  const years = React.useMemo(() => {
    const yearOptions = [];
    for (let year = maxYear ?? 0; year >= (minYear ?? 0); year--) {
      yearOptions.push(year);
    }
    return yearOptions;
  }, [minYear, maxYear]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.target;
    if (!value) {
      onChange?.(null);
    }
    onChange?.(value);
  };

  return (
    <Box sx={{ width: 'inherit' }}>
      <InputLabel label={label} required={required} />
      <TextField
        fullWidth
        error={Boolean(error)}
        helperText={error ? error : null}
        select
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={FORMAT}
        SelectProps={{
          native: true,
        }}
        name={name}
        value={value ?? ''}
        sx={{ minHeight: 47 }}
      >
        <option value="" disabled>
          Select a year
        </option>
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </TextField>
    </Box>
  );
};

export default YearInput;
export type { YearInputProps };
